import { gql } from "apollo-boost";
import { Citations, Feature, QualifiedFeature, MetricValue } from "./shared";

const FindingMetrics = gql`
  fragment FindingMetrics on DiseaseFindingMetrics {
    id
    direction
    category
    presentingSymptom
    finding {
      id
      qfs {
        ...QualifiedFeature
      }
    }
    subcohort {
      id
      qfs {
        ...QualifiedFeature
      }
    }

    metrics {
      sensitivity {
        ...MetricValue
      }
      specificity {
        ...MetricValue
      }
      ppv {
        ...MetricValue
      }
      rrisk_cause {
        ...MetricValue
      }
      rrisk_effect {
        ...MetricValue
      }
      incidence {
        ...MetricValue
      }
      NLR {
        ...MetricValue
      }
      PLR {
        ...MetricValue
      }
    }
  }
  ${QualifiedFeature}
  ${MetricValue}
`;

const DiseaseAgeDistribution = gql`
  fragment DiseaseAgeDistribution on RAgeDistribution {
    ranges {
      from
      to
      value
    }
  }
`;

const UnaryMetrics = gql`
  fragment UnaryMetrics on RUnaryMetrics {
    incidence
    prevalence
    prevalenceDescription
    duration {
      low
      high
      units
    }
    urgency
  }
`;

const DiseaseReferenceData = gql`
  fragment DiseaseReferenceData on DiseaseReference {
    id
    disease {
      ...QualifiedFeature
    }
    findings {
      ...FindingMetrics
    }
    citations @include(if: $withCitations) {
      ...Citations
    }
    citationsDVData @include(if: $withCitations) {
      citationId
      count
      type
      findings {
        id
        qfs {
          ...QualifiedFeature
        }
      }
      subtypes {
        id
        qfs {
          ...QualifiedFeature
        }
      }
    }
    metrics {
      ...UnaryMetrics
    }
  }
  ${QualifiedFeature}
  ${FindingMetrics}
  ${UnaryMetrics}
  ${Citations}
`;

const DiseaseReferenceOverviewData = gql`
  fragment DiseaseReferenceOverviewData on DiseaseReference {
    id
    disease {
      ...QualifiedFeature
      feature {
        parents(depth: 1) {
          features {
            id
          }
        }
        children(depth: 1) {
          features {
            id
          }
        }
        subGroups {
          feature {
            ...Feature
          }
          type
        }
        superGroups {
          feature {
            ...Feature
          }
          type
        }
      }
    }
    metrics {
      ...UnaryMetrics
    }
    ageDistribution {
      ...DiseaseAgeDistribution
    }
  }
  ${QualifiedFeature}
  ${UnaryMetrics}
  ${DiseaseAgeDistribution}
  ${Feature}
`;

export const DiseaseReferenceQuery = {
  DISEASE_BY_FEATURE: gql`
    query drByFeature(
      $featureid: ID!
      $category: String
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = false
      $withCitations: Boolean = false
    ) {
      reference {
        disease(
          disease: { type: QFInput, qf: { feature: $featureid } }
          options: { category: $category }
        ) {
          ...DiseaseReferenceData
          citationsDVData {
            citationId
            count
            findings {
              id
              qfs {
                ...QualifiedFeature
              }
            }
            subtypes {
              id
              qfs {
                ...QualifiedFeature
              }
            }
          }
        }
      }
    }
    ${DiseaseReferenceData}
    ${QualifiedFeature}
  `,
  DISEASE_OVERVIEW: gql`
    query drOverviewByFeature(
      $featureid: ID!
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = false
    ) {
      reference {
        disease(
          disease: { type: QFInput, qf: { feature: $featureid } }
          options: { category: "OVERVIEW" }
        ) {
          ...DiseaseReferenceOverviewData
        }
      }
    }
    ${DiseaseReferenceOverviewData}
  `,

  DISEASE_FINDING_PAIR: gql`
    query drAndFindingPair(
      $featureid: ID!
      $finding: QualifiedFeatureInput
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = false
      $withCitations: Boolean = true
    ) {
      reference {
        diseaseWithFinding(
          disease: { type: QFInput, qf: { feature: $featureid } }
          finding: { type: QFInput, qf: $finding }
          options: {}
        ) {
          ...DiseaseReferenceData
        }
      }
    }
    ${DiseaseReferenceData}
  `,
  DISEASE_COHORT_PAIR: gql`
    query drAndCohortPair(
      $diseaseCohortid: ID!
      $cohortid: ID!
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = false
      $withCitations: Boolean = true
    ) {
      reference {
        diseaseWithFinding(
          disease: { type: CohortId, cohortId: $diseaseCohortid }
          finding: { type: CohortId, cohortId: $cohortid }
          options: {}
        ) {
          ...DiseaseReferenceData
        }
      }
    }
    ${DiseaseReferenceData}
  `,
};
